import { Star, Quote, ShieldCheck, ArrowRight } from "lucide-react";

import { homeTestimonials } from "@/data/homeTestimonials";
import { googleReviewSummary, googleRatingText } from "@/data/googleReviews";

/**
 * Homepage testimonials.
 *
 * Reviews come from `src/data/homeTestimonials.ts` and the Google rating /
 * review count from `src/data/googleReviews.ts`. Neither is ever invented —
 * until real Google figures are supplied, bracketed placeholders show instead.
 */
const HomeTestimonials = () => {
  const reviews = homeTestimonials;

  return (
    <section className="bg-card border-y border-border">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.28em] text-clay mb-4">Client Feedback</p>
          <h2 className="font-heading text-3xl md:text-4xl text-foreground leading-tight">
            What Our Clients Say
          </h2>
          <p className="mt-5 text-sm md:text-base text-muted-foreground leading-relaxed">
            What homeowners and business owners say about working with us.
          </p>
        </div>

        {/* Google rating summary */}
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5 border border-border bg-background p-6 md:p-8 rounded-sm shadow-soft">
          <div className="flex items-center gap-3">
            <span className="font-heading text-xl text-foreground" aria-hidden="true">
              Google
            </span>
          </div>
          <p className="text-foreground sm:border-l sm:border-border sm:pl-5 flex-1">
            {googleRatingText()}
          </p>
          <a
            href={googleReviewSummary.profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-clay hover:bg-clay-dark text-primary-foreground px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors"
          >
            Read Our Google Reviews
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {reviews.length === 0 ? (
          <p className="mt-6 text-sm text-muted-foreground flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-clay shrink-0" />
            Licensed &amp; insured plaster and painting contractor serving Bergen, Passaic, Essex,
            Morris &amp; Hudson Counties from Wayne, NJ.
          </p>
        ) : (
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {reviews.slice(0, 3).map((review) => (
              <a
                key={`${review.name}-${review.location}`}
                href={googleReviewSummary.profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full transition-colors"
              >
              <blockquote className="h-full bg-background border border-border hover:border-clay p-8 rounded-sm shadow-soft flex flex-col transition-colors">
                <Quote className="w-6 h-6 text-clay/60" />
                <p className="mt-5 text-[15px] md:text-base text-foreground/85 leading-relaxed flex-1">
                  "{review.quote}"
                </p>
                <div
                  className="mt-6 flex items-center gap-1"
                  role="img"
                  aria-label={`${review.rating} star rating`}
                >
                  {Array.from({ length: review.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-clay text-clay" />
                  ))}
                </div>
                <footer className="mt-3 text-sm text-muted-foreground">
                  {review.name} — {review.location}
                </footer>
              </blockquote>
              </a>
            ))}
          </div>
        )}


      </div>
    </section>
  );
};

export default HomeTestimonials;
